import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";
import { printPaymentReceipt } from "@/lib/paymentReceipt";

/**
 * Retail-Dues Detail Report — every APPROVED sale with a residual due > 0,
 * detailed by chassis + customer + age (days).
 *
 * Each row has a **Collect** button that opens `CollectDueDialog` to record
 * a payment against that sale (posts to /sales/{id}/reconcile-due with
 * mode=collected). On success the receipt is auto-printed.
 */
export function RetailDuesDetailReport({ branchId }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_due: 0, row_count: 0, migrated_excluded: 0 });
  const [loading, setLoading] = useState(false);
  const [collectFor, setCollectFor] = useState(null); // row being collected against

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      const { data } = await api.get("/reports/retail-dues-detail", { params });
      setRows(data.rows || []);
      setTotals({
        total_due: data.total_due || 0,
        row_count: data.row_count || 0,
        migrated_excluded: data.migrated_excluded || 0,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div data-testid="retail-dues-detail-report" className="bg-canvas-card border border-canvas-line">
      <div className="px-4 py-3 border-b border-canvas-line flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Retail Dues — By Chassis + Customer</div>
          <div className="text-sm">
            <b>{totals.row_count}</b> sales with residual due — total <b>{fmtMoney(totals.total_due)}</b>
          </div>
          {totals.migrated_excluded > 0 && (
            <div className="text-[10px] font-mono text-ink-muted mt-0.5">
              {totals.migrated_excluded} migrated / imported sale(s) with pre-Emergent shortfall are hidden from this report.
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-canvas-panel border-b border-canvas-line">
            <tr>
              {["Sale Date", "Branch", "Chassis #", "Model", "Customer", "Phone", "On-Road ₹", "Due ₹", "Type", "Financier", "Age (d)", ""].map((h) => (
                <th key={h} className="text-left px-2 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={12} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-12 text-center font-mono text-xs text-ink-subtle">No dues outstanding — every sale is settled ✓</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.sale_id} className="border-b border-canvas-line last:border-b-0 hover:bg-canvas-panel/40" data-testid={`due-row-${r.sale_id}`}>
                <td className="px-2 py-2 font-mono text-[11px]">{r.sale_date || "—"}</td>
                <td className="px-2 py-2">{r.branch_name || "—"}</td>
                <td className="px-2 py-2 font-mono text-[11px]">{r.chassis_no || "—"}</td>
                <td className="px-2 py-2">{r.vehicle_model || "—"}</td>
                <td className="px-2 py-2 font-semibold">{r.customer_name || "—"}</td>
                <td className="px-2 py-2 font-mono text-[11px]">{r.customer_phone || "—"}</td>
                <td className="px-2 py-2 font-mono tabular-nums text-right">{fmtMoney(r.on_road_price)}</td>
                <td className="px-2 py-2 font-mono tabular-nums text-right text-destructive font-semibold">{fmtMoney(r.due_amount)}</td>
                <td className="px-2 py-2 font-mono text-[10px] uppercase">{r.sale_type}</td>
                <td className="px-2 py-2">{r.financier_name || "—"}</td>
                <td className="px-2 py-2 text-center">
                  <span className={`inline-block px-1.5 py-0.5 border text-[10px] font-mono ${r.days_pending > 60 ? "border-destructive text-destructive" : r.days_pending > 30 ? "border-[#B67100] text-[#B67100]" : "border-canvas-line"}`}>
                    {r.days_pending}d
                  </span>
                </td>
                <td className="px-2 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => setCollectFor(r)}
                    data-testid={`due-collect-${r.sale_id}`}
                    className="px-2 py-1 border border-[#00713a] text-[#00713a] text-[10px] font-mono uppercase tracking-wider hover:bg-[#00713a] hover:text-white"
                  >
                    Collect
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {collectFor && (
        <CollectDueDialog
          row={collectFor}
          onClose={() => setCollectFor(null)}
          onCollected={() => { setCollectFor(null); fetchData(); }}
        />
      )}
    </div>
  );
}


/**
 * CollectDueDialog — records a receipt against a due sale.
 * Calls POST /api/sales/{sale_id}/reconcile-due with mode=collected.
 * On success it fetches the freshly-inserted payment_line + dealership
 * meta and hands off to `printPaymentReceipt` to open a print window.
 */
function CollectDueDialog({ row, onClose, onCollected }) {
  const [form, setForm] = useState({
    amount: row.due_amount,
    payment_method: "cash",
    reference: "",
    paid_on: new Date().toISOString().slice(0, 10),
    note: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be positive"); return; }
    if (amt > row.due_amount + 0.01) {
      toast.error(`Amount ₹${amt.toLocaleString("en-IN")} exceeds outstanding ₹${row.due_amount.toLocaleString("en-IN")}`);
      return;
    }
    if (form.payment_method !== "cash" && !form.reference.trim()) {
      toast.error(`${form.payment_method.toUpperCase()} needs a reference / UTR`);
      return;
    }
    setSaving(true);
    try {
      const { data: sale } = await api.post(`/sales/${row.sale_id}/reconcile-due`, {
        mode: "collected",
        amount: amt,
        payment_method: form.payment_method,
        reference: form.reference.trim(),
        paid_on: form.paid_on,
        note: form.note.trim(),
      });
      toast.success("Due collected — printing receipt");
      // Print the receipt (thermal by default). Pull the last _reconciled line
      // and hydrate with dealership + customer meta available on the sale doc.
      const line = (sale.payment_lines || []).filter((l) => l._reconciled).slice(-1)[0];
      let dealership = {};
      try {
        const { data } = await api.get("/settings/dealership");
        dealership = data || {};
      } catch { /* dealership meta optional */ }
      const outstandingAfter = Math.max(0, (Number(sale.due) || 0));
      printPaymentReceipt({
        dealership,
        receipt_no: line?.receipt_no || "",
        paid_on: line?.paid_on || form.paid_on,
        now: new Date().toISOString(),
        customer: sale.customer || { name: row.customer_name, phone: row.customer_phone },
        vehicle: sale.vehicle || { vin: row.chassis_no, model: row.vehicle_model },
        sale: { invoice_no: sale.invoice_no || "", on_road_price: sale.on_road_price },
        line: line || { method: form.payment_method, amount: amt, reference: form.reference, paid_on: form.paid_on, _reconciled: true },
        outstanding_after: outstandingAfter,
        received_by: "",
      }, { format: "thermal", autoPrint: true });
      onCollected();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="collect-due-modal">
      <div className="bg-white w-full max-w-md border border-canvas-line">
        <div className="px-5 py-3 border-b border-canvas-line flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Collect Due</div>
            <div className="text-sm font-semibold">{row.customer_name} · <span className="font-mono">{row.chassis_no}</span></div>
            <div className="text-[11px] text-ink-muted">Outstanding: <b className="text-destructive">{fmtMoney(row.due_amount)}</b></div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink text-xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Amount ₹</label>
              <input type="number" step="0.01" min="0.01" value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                data-testid="collect-due-amount"
                className="w-full px-3 py-2 border border-canvas-line bg-canvas text-sm font-mono tabular-nums" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Paid on</label>
              <input type="date" value={form.paid_on}
                onChange={(e) => set("paid_on", e.target.value)}
                data-testid="collect-due-paid-on"
                className="w-full px-3 py-2 border border-canvas-line bg-canvas text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Method</label>
            <select value={form.payment_method}
              onChange={(e) => set("payment_method", e.target.value)}
              data-testid="collect-due-method"
              className="w-full px-3 py-2 border border-canvas-line bg-canvas text-sm">
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="card">Card</option>
              <option value="online">Online / NEFT</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">
              Reference {form.payment_method !== "cash" && <span className="text-destructive">*</span>}
            </label>
            <input type="text" value={form.reference}
              onChange={(e) => set("reference", e.target.value)}
              placeholder="UTR / txn id / card slip #"
              data-testid="collect-due-ref"
              className="w-full px-3 py-2 border border-canvas-line bg-canvas text-sm font-mono" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Note (optional)</label>
            <input type="text" value={form.note}
              onChange={(e) => set("note", e.target.value)}
              data-testid="collect-due-note"
              className="w-full px-3 py-2 border border-canvas-line bg-canvas text-sm" />
          </div>
          <div className="text-[11px] text-ink-muted bg-canvas-panel p-2 border border-canvas-line">
            A receipt with a unique receipt number will be generated and printed automatically. This line will appear in Receipts Ledger.
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-canvas-line text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="collect-due-submit"
              className="px-4 py-2 bg-[#00713a] text-white text-sm hover:bg-[#00B341] disabled:opacity-50">
              {saving ? "Recording…" : "Collect & Print Receipt"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Simple branch-level summary — used by both Write-Off and Excess reports.
 */
function BranchSummaryTable({ endpoint, branchId, dateFrom, dateTo, amountKey, title, emptyMsg, testId }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ grand_total: 0, grand_count: 0 });
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get(endpoint, { params });
      setRows(data.rows || []);
      setTotals({ grand_total: data.grand_total || 0, grand_count: data.grand_count || 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [endpoint, branchId, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="bg-canvas-card border border-canvas-line" data-testid={testId}>
      <div className="px-4 py-3 border-b border-canvas-line">
        <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{title}</div>
        <div className="text-sm">
          <b>{totals.grand_count}</b> sales — total <b>{fmtMoney(totals.grand_total)}</b>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-canvas-panel border-b border-canvas-line">
          <tr>
            {["Branch", "# Sales", "Amount ₹"].map((h) => (
              <th key={h} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={3} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">Loading…</td></tr>}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={3} className="px-4 py-12 text-center font-mono text-xs text-ink-subtle">{emptyMsg}</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.branch_id} className="border-b border-canvas-line last:border-b-0 hover:bg-canvas-panel/40">
              <td className="px-3 py-2 font-semibold">{r.branch_name || r.branch_id}</td>
              <td className="px-3 py-2 font-mono tabular-nums">{r.sale_count}</td>
              <td className="px-3 py-2 font-mono tabular-nums font-semibold">{fmtMoney(r[amountKey])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function WriteOffSummaryReport({ branchId, dateFrom, dateTo }) {
  return (
    <BranchSummaryTable
      endpoint="/reports/writeoff-summary"
      branchId={branchId}
      dateFrom={dateFrom}
      dateTo={dateTo}
      amountKey="writeoff_amount"
      title="Write-Off — By Branch"
      emptyMsg="No write-offs in the selected window."
      testId="writeoff-summary-report"
    />
  );
}

export function ExcessSummaryReport({ branchId, dateFrom, dateTo }) {
  return (
    <BranchSummaryTable
      endpoint="/reports/excess-summary"
      branchId={branchId}
      dateFrom={dateFrom}
      dateTo={dateTo}
      amountKey="excess_amount"
      title="Excess (Customer Over-paid) — By Branch"
      emptyMsg="No excess collection detected."
      testId="excess-summary-report"
    />
  );
}

/**
 * WriteOffExcessCombinedReport — one table showing both Write-Off and
 * Excess totals per branch, side-by-side. Fetches the two summary
 * endpoints in parallel and merges by branch_id.
 */
export function WriteOffExcessCombinedReport({ branchId, dateFrom, dateTo }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ wo_total: 0, wo_count: 0, ex_total: 0, ex_count: 0 });
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [woRes, exRes] = await Promise.all([
        api.get("/reports/writeoff-summary", { params }),
        api.get("/reports/excess-summary", { params }),
      ]);
      // Merge by branch_id.
      const merged = new Map();
      const upsert = (branch_id, patch) => {
        const prev = merged.get(branch_id) || {
          branch_id,
          branch_name: patch.branch_name || branch_id,
          writeoff_amount: 0, writeoff_count: 0,
          excess_amount: 0, excess_count: 0,
        };
        merged.set(branch_id, { ...prev, ...patch, branch_name: prev.branch_name || patch.branch_name });
      };
      (woRes.data.rows || []).forEach((r) => upsert(r.branch_id, {
        branch_name: r.branch_name,
        writeoff_amount: r.writeoff_amount,
        writeoff_count: r.sale_count,
      }));
      (exRes.data.rows || []).forEach((r) => {
        const prev = merged.get(r.branch_id);
        upsert(r.branch_id, {
          branch_name: prev?.branch_name || r.branch_name,
          excess_amount: r.excess_amount,
          excess_count: r.sale_count,
          writeoff_amount: prev?.writeoff_amount ?? 0,
          writeoff_count: prev?.writeoff_count ?? 0,
        });
      });
      const list = [...merged.values()].sort(
        (a, b) => (b.writeoff_amount + b.excess_amount) - (a.writeoff_amount + a.excess_amount)
      );
      setRows(list);
      setTotals({
        wo_total: woRes.data.grand_total || 0,
        wo_count: woRes.data.grand_count || 0,
        ex_total: exRes.data.grand_total || 0,
        ex_count: exRes.data.grand_count || 0,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [branchId, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="bg-canvas-card border border-canvas-line" data-testid="writeoff-excess-combined-report">
      <div className="px-4 py-3 border-b border-canvas-line">
        <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Write-Off &amp; Excess — By Branch</div>
        <div className="text-sm flex gap-6 mt-1">
          <span>
            <span className="text-ink-muted">Write-Off:</span> <b>{totals.wo_count}</b> sales · <b className="text-destructive">{fmtMoney(totals.wo_total)}</b>
          </span>
          <span>
            <span className="text-ink-muted">Excess:</span> <b>{totals.ex_count}</b> sales · <b className="text-[#00713a]">{fmtMoney(totals.ex_total)}</b>
          </span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line">
            <tr>
              <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted" rowSpan={2}>Branch</th>
              <th className="text-center px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted border-l border-canvas-line" colSpan={2}>Write-Off</th>
              <th className="text-center px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted border-l border-canvas-line" colSpan={2}>Excess</th>
            </tr>
            <tr>
              <th className="text-right px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted border-l border-canvas-line"># Sales</th>
              <th className="text-right px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Amount ₹</th>
              <th className="text-right px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted border-l border-canvas-line"># Sales</th>
              <th className="text-right px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Amount ₹</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center font-mono text-xs text-ink-subtle">No write-off or excess activity in the selected window.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.branch_id} className="border-b border-canvas-line last:border-b-0 hover:bg-canvas-panel/40" data-testid={`wo-ex-row-${r.branch_id}`}>
                <td className="px-3 py-2 font-semibold">{r.branch_name || r.branch_id}</td>
                <td className="px-3 py-2 font-mono tabular-nums text-right border-l border-canvas-line">{r.writeoff_count || 0}</td>
                <td className={`px-3 py-2 font-mono tabular-nums text-right ${r.writeoff_amount > 0 ? "text-destructive font-semibold" : "text-ink-muted"}`}>
                  {r.writeoff_amount > 0 ? fmtMoney(r.writeoff_amount) : "—"}
                </td>
                <td className="px-3 py-2 font-mono tabular-nums text-right border-l border-canvas-line">{r.excess_count || 0}</td>
                <td className={`px-3 py-2 font-mono tabular-nums text-right ${r.excess_amount > 0 ? "text-[#00713a] font-semibold" : "text-ink-muted"}`}>
                  {r.excess_amount > 0 ? fmtMoney(r.excess_amount) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-canvas-panel/60 border-t-2 border-canvas-line">
              <tr>
                <td className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Total</td>
                <td className="px-3 py-2 font-mono tabular-nums text-right border-l border-canvas-line font-semibold">{totals.wo_count}</td>
                <td className="px-3 py-2 font-mono tabular-nums text-right text-destructive font-semibold">{fmtMoney(totals.wo_total)}</td>
                <td className="px-3 py-2 font-mono tabular-nums text-right border-l border-canvas-line font-semibold">{totals.ex_count}</td>
                <td className="px-3 py-2 font-mono tabular-nums text-right text-[#00713a] font-semibold">{fmtMoney(totals.ex_total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
